import { Box, ButtonBase, Tooltip } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import Konva from 'konva';
import moment from 'moment';
import React, { useMemo, forwardRef } from 'react';

import { useResponsiveFlags } from '../../../shared/lib';
import { CalendarWidget } from './calendarWidget';
import { ColorsWidget } from './colorsWidget';
import { ColorsWidgetAdaptive } from './colorsWidgetAdaptive';
import { SchemaContainer, SchemaContainerRef } from './schemaContainer';
import { SchemaSwitcherWidget } from './schemaSwitcherWidget';
import { TableGridWidget } from './tableGridWidget';

export const SchemaWidget = forwardRef<
  SchemaContainerRef,
  {
    sections: GM.ReservesSchemas.SectionDTO[];
    onSelectSection: (sectionId: number) => void;
    sectionId?: number;
    selectedTableIds?: number[];
    onClickSchema?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    onClickTable: (id: number) => void;
    tablesSchedule: GM.ReservesSchemas.TablesSchedule;
    offsetWidth: number;
    onInteractionStart?: () => void;
    onInteractionEnd?: () => void;
    onTableHighlight?: (tableId: number | null) => void;
    date?: number;
    onChangeDate?: (date: moment.Moment) => void;
  }
>(
  (
    {
      sections,
      onSelectSection,
      sectionId,
      selectedTableIds,
      onClickSchema,
      onClickTable,
      tablesSchedule,
      offsetWidth,
      onInteractionStart,
      onInteractionEnd,
      onTableHighlight,
      date,
      onChangeDate,
    },
    ref,
  ) => {
    const section = sections.find((section) => section.id === sectionId);
    const { isDesktop, isMobilePortrait } = useResponsiveFlags();

    // Определяем, нужно ли сворачивать легенду через брейкпоинты
    // Сворачиваем на экранах меньше 1000px (порог мобильного отображения)
    const shouldCollapseLegend = !isDesktop;

    const isGridOnly = useMemo(() => {
      if (!section || !section.schema) {
        return true;
      }

      for (const containerName of ['images', 'tables', 'marks', 'rectangles', 'ellipses']) {
        if (section.schema[containerName] && section.schema[containerName].length > 0) {
          return false;
        }
      }

      return true;
    }, [section]);

    if (!section) {
      return null;
    }

    const renderHelpButton = (onClick: () => void) => {
      const helpButton = (
        <ButtonBase
          onClick={onClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 8px',
            borderRadius: 6,
            fontSize: 14,
            color: 'rgb(70, 79, 92)',
            cursor: 'pointer',
          }}
        >
          <HelpOutlineIcon style={{ fontSize: 18 }} />
        </ButtonBase>
      );
      return isDesktop ? (
        <Tooltip
          title="Что означают цвета столов?"
          placement="bottom"
          arrow
          enterDelay={300}
          leaveDelay={100}
        >
          {helpButton}
        </Tooltip>
      ) : (
        helpButton
      );
    };

    return (
      <Box display="flex" flexDirection="column" height="100%">
        <Box
          flexGrow={0}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom={shouldCollapseLegend ? '24px' : undefined}
          minHeight="40px"
        >
          <Box flexShrink={1} minWidth={0}>
            <SchemaSwitcherWidget
              sections={sections}
              onSelectSection={onSelectSection}
              sectionId={sectionId}
            />
          </Box>
          {isMobilePortrait && date && onChangeDate && (
            <Box
              flexGrow={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ margin: '0 8px' }}
            >
              <CalendarWidget date={date} onChangeDate={onChangeDate} hideTodayButton />
            </Box>
          )}
          {shouldCollapseLegend && (
            <Box flexShrink={0}>
              <ColorsWidgetAdaptive renderCollapsedButton={renderHelpButton} />
            </Box>
          )}
        </Box>

        {!shouldCollapseLegend && (
          <Box flexGrow={0} marginTop="24px" marginBottom="20px">
            <ColorsWidget />
          </Box>
        )}

        {isGridOnly && (
          <TableGridWidget
            section={section}
            selectedTableIds={selectedTableIds}
            onClickTable={onClickTable}
            tablesSchedule={tablesSchedule}
            onClickSchema={onClickSchema}
          />
        )}

        {!isGridOnly && (
          <Box flexGrow={1} flexShrink={1} minHeight={0} style={{ overflow: 'hidden' }}>
            <SchemaContainer
              ref={ref}
              section={section}
              selectedTableIds={selectedTableIds}
              onClickTable={onClickTable}
              tablesSchedule={tablesSchedule}
              onClickSchema={onClickSchema}
              offsetWidth={offsetWidth}
              onInteractionStart={onInteractionStart}
              onInteractionEnd={onInteractionEnd}
              onTableHighlight={onTableHighlight}
            />
          </Box>
        )}
      </Box>
    );
  },
);

SchemaWidget.displayName = 'SchemaWidget';
